 <template>
  <div>
    <div class="filters-section mb-4">
      <b-card>
        <b-card-header class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
              <i class="fas fa-filter mr-2"></i>Filters
          </h5>
          <div class="">
            <b-button @click="exportReport('csv')" variant="outline-primary" class="mr-2">
              <i class="bi bi-download"></i> Export CSV
            </b-button>
            <b-button @click="exportReport('pdf')" variant="outline-secondary">
              <i class="bi bi-file-pdf"></i> Export PDF
            </b-button>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col md="3">
              <b-form-group label="Date From:" label-for="dateFrom">
                <b-form-datepicker
                  id="dateFrom"
                  v-model="filters.dateFrom"
                  @input="fetchReport"
                  class="date-picker"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Date To:" label-for="dateTo">
                <b-form-datepicker
                  id="dateTo"
                  v-model="filters.dateTo"
                  @input="fetchReport"
                  class="date-picker"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Period:" label-for="period">
                <b-form-select
                  id="period"
                  v-model="filters.period"
                  :options="periodOptions"
                  @change="fetchReport"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Hotel:" label-for="hotel">
                <b-form-select
                  id="hotel"
                  v-model="filters.hotelId"
                  :options="hotelOptions"
                  @change="fetchReport"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Payment Status:" label-for="paymentStatus">
                <b-form-select
                  id="paymentStatus"
                  v-model="filters.paymentStatus"
                  :options="paymentStatusOptions"
                  @change="fetchReport"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </div>

    <!-- Summary Cards -->
    <div class="summary-cards mb-4" v-if="reportData.summary">
      <b-row>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Total Revenue</h4>
              <p class="amount positive">${{ formatCurrency(reportData.summary.total_revenue) }}</p>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Total Discount</h4>
              <p class="amount negative">${{ formatCurrency(reportData.summary.total_discount) }}</p>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Net Revenue</h4>
              <p class="amount positive">${{ formatCurrency(reportData.summary.net_revenue) }}</p>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Total Bookings</h4>
              <p class="amount">{{ formatNumber(reportData.summary.total_bookings) }}</p>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Avg Booking Value</h4>
              <p class="amount positive">${{ formatCurrency(reportData.summary.average_booking_value) }}</p>
            </div>
          </b-card>
        </b-col>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Period</h4>
              <p class="period">{{ formatPeriod(reportData.summary.period) }}</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Tabs for different views -->
    <b-tabs card class="custom-tabs" nav-class="mb-3" v-model="activeTab">
      <b-tab title="Revenue by Period" active>
        <div class="section">
          <b-table 
            :items="reportData.revenue_by_period" 
            :fields="periodFields" 
            :per-page="perPage"
            :current-page="currentPage"
            striped 
            hover 
            responsive
            class="mt-3"
          >
            <template #cell(total_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.total_revenue) }}</span>
            </template>
            <template #cell(total_discount)="data">
              <span class="amount negative">${{ formatCurrency(data.item.total_discount) }}</span>
            </template>
            <template #cell(net_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.net_revenue) }}</span>
            </template>
            <template #cell(average_booking_value)="data">
              <span class="amount positive">${{ formatCurrency(data.item.average_booking_value) }}</span>
            </template>
          </b-table>
          <b-pagination
            v-model="currentPage"
            :total-rows="reportData.revenue_by_period.length"
            :per-page="perPage"
            class="mt-3"
          ></b-pagination>
        </div>
      </b-tab>

      <b-tab title="Revenue by Hotel">
        <div class="section">
          <b-table 
            :items="reportData.revenue_by_hotel" 
            :fields="hotelFields" 
            :per-page="perPage"
            :current-page="currentPage"
            striped 
            hover 
            responsive
            class="mt-3"
          >
            <template #cell(total_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.total_revenue) }}</span>
            </template>
            <template #cell(total_discount)="data">
              <span class="amount negative">${{ formatCurrency(data.item.total_discount) }}</span>
            </template>
            <template #cell(net_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.net_revenue) }}</span>
            </template>
            <template #cell(average_booking_value)="data">
              <span class="amount positive">${{ formatCurrency(data.item.average_booking_value) }}</span>
            </template>
          </b-table>
          <b-pagination
            v-model="currentPage"
            :total-rows="reportData.revenue_by_hotel.length"
            :per-page="perPage"
            class="mt-3"
          ></b-pagination>
        </div>
      </b-tab>

      <b-tab title="Revenue by Payment Status">
        <div class="section">
          <b-table 
            :items="reportData.revenue_by_payment_status" 
            :fields="paymentFields" 
            striped 
            hover 
            responsive
            class="mt-3"
          >
            <template #cell(payment_status)="data">
              <span :class="getPaymentStatusBadgeClass(data.item.payment_status)">
                {{ getPaymentStatusLabel(data.item.payment_status) }}
              </span>
            </template>
            <template #cell(total_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.total_revenue) }}</span>
            </template>
            <template #cell(total_discount)="data">
              <span class="amount negative">${{ formatCurrency(data.item.total_discount) }}</span>
            </template>
            <template #cell(net_revenue)="data">
              <span class="amount positive">${{ formatCurrency(data.item.net_revenue) }}</span>
            </template>
            <template #cell(percentage)="data">
              <span class="percentage">{{ formatNumber(data.item.percentage) }}%</span>
            </template>
          </b-table>
          <b-pagination
            v-model="currentPage"
            :total-rows="reportData.revenue_by_payment_status.length"
            :per-page="perPage"
            class="mt-3"
          ></b-pagination>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import axios from '@/axios';
import { request } from '@/Util/Request';

export default {
  name: 'BookingRevenueReport',
  data() {
    return {
      filters: {
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10), // 30 days ago
        dateTo: new Date().toISOString().slice(0, 10),
        period: 'daily',
        hotelId: null,
        paymentStatus: null
      },
      reportData: {
        summary: null,
        revenue_by_period: [],
        revenue_by_hotel: [],
        revenue_by_payment_status: []
      },
      periodOptions: [
        { value: 'daily', text: 'Daily' },
        { value: 'monthly', text: 'Monthly' },
        { value: 'yearly', text: 'Yearly' }
      ],
      periodFields: [
        { key: 'period', label: 'Period', sortable:true },
        { key: 'total_revenue', label: 'Total Revenue', sortable:true },
        { key: 'total_discount', label: 'Total Discount', sortable:true },
        { key: 'net_revenue', label: 'Net Revenue', sortable:true },
        { key: 'booking_count', label: 'Booking Count', sortable:true },
        { key: 'average_booking_value', label: 'Avg Booking Value', sortable:true }
      ],
      hotelFields: [
        { key: 'hotel_name', label: 'Hotel', sortable:true },
        { key: 'total_revenue', label: 'Total Revenue', sortable:true },
        { key: 'total_discount', label: 'Total Discount', sortable:true },
        { key: 'net_revenue', label: 'Net Revenue', sortable:true },
        { key: 'booking_count', label: 'Booking Count', sortable:true },
        { key: 'average_booking_value', label: 'Avg Booking Value', sortable:true }
      ],
      paymentFields: [
        { key: 'payment_status', label: 'Payment Status', sortable:true },
        { key: 'total_revenue', label: 'Total Revenue', sortable:true },
        { key: 'total_discount', label: 'Total Discount', sortable:true },
        { key: 'net_revenue', label: 'Net Revenue', sortable:true },
        { key: 'booking_count', label: 'Booking Count', sortable:true },
        { key: 'percentage', label: 'Percentage', sortable:true }
      ],
      perPage: 10,
      currentPage: 1,
      activeTab:0
    }
  },
  props: {
    hotelOptions:{
      type: Array,
      required:true
    },
    paymentStatusOptions: {
      type: Array,
      required:true
    },
  },
  methods: {
    async fetchReport() {
      try {
        const params = {
          date_from: this.filters.dateFrom,
          date_to: this.filters.dateTo,
          period: this.filters.period,
          hotel_id: this.filters.hotelId,
          payment_status: this.filters.paymentStatus
        };

        const response = await axios.get('/reports/booking-revenue', { params });
        this.reportData = response.data;
      } catch (error) {
        console.error('Error fetching report:', error);
        this.$bvToast.toast('Error fetching report data', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    async exportReport(format) {
      try {
        const params = {
          date_from: this.filters.dateFrom,
          date_to: this.filters.dateTo,
          period: this.filters.period,
          hotel_id: this.filters.hotelId,
          payment_status: this.filters.paymentStatus,
          format: format
        };

        const response = await request({
          url: `/reports/booking-revenue`,
          method: 'GET',
          params: params,
          responseType: 'blob',
        });

        const mimeType = format === 'csv' 
          ? 'text/csv' 
          : 'application/pdf';
        
        const blob = new Blob([response], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `booking_revenue_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$bvToast.toast(`Report exported successfully as ${format.toUpperCase()}`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Export failed:', error);
        this.$bvToast.toast('Error exporting report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0);
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US').format(value || 0);
    },

    formatPeriod(period) {
      return period.charAt(0).toUpperCase() + period.slice(1);
    },

    getPaymentStatusBadgeClass(status) {
      switch (status) {
        case 'paid': return 'badge bg-success text-white';
        case 'pending': return 'badge bg-warning text-dark';
        case 'failed': return 'badge bg-danger text-white';
        case 'cancelled': return 'badge bg-secondary text-white';
        default: return 'badge bg-secondary text-white';
      }
    },

    getPaymentStatusLabel(status) {
      switch (status) {
        case 'paid': return 'Paid';
        case 'pending': return 'Pending';
        case 'failed': return 'Failed';
        case 'cancelled': return 'Cancelled';
        default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
      }
    },
  },
  mounted() {
    this.fetchReport();
  },
  watch: {
    activeTab(newVal,oldVal) {
      if (newVal !== oldVal) {
        this.perPage = 10; // or any default value you prefer
        this.currentPage = 1;
      }
    }
  }
}
</script>

<style scoped>
.report-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filters-section {
  background: #f8fafc;
  border-radius: 8px;
}

.custom-tabs .nav-link.active {
  background: #2563eb !important;
  color: #fff !important;
  border-radius: 999px !important;
  font-weight: 600;
  box-shadow: 0 2px 8px #2563eb22;
}

.custom-tabs .nav-link {
  border-radius: 999px !important;
  margin-right: 0.5rem;
  color: #2563eb !important;
  background: #f3f4f6 !important;
  transition: background 0.2s, color 0.2s;
}

.summary-cards {
  margin-bottom: 2rem;
}

.summary-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  transition: transform 0.2s;
}

.summary-card:hover {
  transform: translateY(-2px);
}

.summary-item {
  text-align: center;
}

.summary-item h4 {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.summary-item .amount {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
}

.summary-item .amount.positive {
  color: #059669;
}

.summary-item .amount.negative {
  color: #dc2626;
}

.summary-item .period {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2563eb;
  margin: 0;
}

.amount.positive {
  color: #059669;
  font-weight: 600;
}

.amount.negative {
  color: #dc2626;
  font-weight: 600;
}

.percentage {
  font-weight: 600;
  color: #6b7280;
}

.badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  font-size: 0.85em;
  font-weight: 600;
  border-radius: 999px;
  text-transform: capitalize;
  letter-spacing: 0.02em;
}

.bg-success {
  background: #22c55e !important;
  color: #fff !important;
}

.bg-warning {
  background: #fbbf24 !important;
  color: #222 !important;
}

.bg-danger {
  background: #ef4444 !important;
  color: #fff !important;
}

.bg-secondary {
  background: #6b7280 !important;
  color: #fff !important;
}

.date-picker {
  min-width: 150px;
}

@media (max-width: 768px) {
  .report-title {
    font-size: 1.1rem;
  }
  
  .summary-item .amount {
    font-size: 1.25rem;
  }
}
</style>